import { useDeleteClipShare } from "@/hooks/useClipShares";
import { FC, useState, useEffect } from "react";
import { IconButton, Button, CircularProgress } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackAlert } from "../../useSnackAlert";


export const DeleteConfirm: FC<{
  clipShareId: number;
  bookmarkId: number;
  handleClose: () => void;
}> = ({ clipShareId, bookmarkId, handleClose }) => {
  const { showSnackbar } = useSnackAlert();
  const [countdown, setCountdown] = useState(2);
  const [canConfirm, setCanConfirm] = useState(false);
  
  const { mutateAsync: deleteClipShare, isPending: isDeleting } = useDeleteClipShare(() => {
    showSnackbar("Clip share deleted successfully", "success");
    handleClose();
  });

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanConfirm(true);
    }
  }, [countdown]);

  const handleDelete = async () => {
    try {
      await deleteClipShare({ id: clipShareId, bookmarkId });
    } catch (_error) {
      showSnackbar("Error deleting clip share", "error");
    }
  };

  return (
    <div className={`px-4 py-3 min-w-[280px] rounded-lg transition-all duration-300 ${
      isDeleting ? 'bg-gray-300' : ''
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-base font-semibold text-brand-1 flex-1">
          Remove Clip
        </div>
        <IconButton
          size="small"
          onClick={handleClose}
          className="ml-2 text-neutral-700 hover:bg-neutral-100"
          disabled={isDeleting}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>
      
      <div className="text-sm text-neutral-900 mb-4 font-medium">
        You will remove the following clip. The share link will remain active.
      </div>
      
      {isDeleting && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-[#7fa4ca] text-white rounded-lg animate-pulse">
          <CircularProgress size={18} sx={{ color: 'white' }} />
          <span className="text-sm font-medium">Just a moment, working on it...</span>
        </div>
      )}
      
      <div className="flex items-center justify-end gap-3">
        <Button
          size="small"
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={!canConfirm || isDeleting}
          startIcon={!isDeleting ? <DeleteIcon /> : null}
          className={`text-[0.813rem] px-2 transition-opacity duration-300 ease ${
            canConfirm && !isDeleting ? 'opacity-100' : 'opacity-40'
          }`}
        >
          {isDeleting ? 'Working on it...' : canConfirm ? 'Delete' : `Delete (${countdown})`}
        </Button>
      </div>
    </div>
  );
};